'use client';

import * as React from 'react';
import { X, Plus, Tag as TagIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from "@/lib/utils";

interface CustomerTagsBadgeProps {
  tags: string[];
  onTagAdd?: (tag: string) => Promise<void>;
  onTagRemove?: (tag: string) => Promise<void>;
  editable?: boolean;
  maxVisible?: number;
  className?: string;
}

/**
 * Display and manage customer tags with add/remove functionality
 */
export function CustomerTagsBadge({
  tags,
  onTagAdd,
  onTagRemove,
  editable = false,
  maxVisible = 3,
  className,
}: CustomerTagsBadgeProps) {
  const { t } = useTranslation('customers');
  const [newTag, setNewTag] = React.useState('');
  const [isAdding, setIsAdding] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const visibleTags = tags.slice(0, maxVisible);
  const hiddenCount = Math.max(0, tags.length - maxVisible);

  const handleAddTag = async () => {
    if (!newTag.trim() || !onTagAdd) return;

    setIsAdding(true);
    try {
      await onTagAdd(newTag.trim());
      setNewTag('');
    } catch (error) {
      console.error('Failed to add tag:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    if (!onTagRemove) return;

    try {
      await onTagRemove(tag);
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };


  const formatTag = (tag: string): { prefix: string; value: string } | null => {
    if (!tag.includes(':')) return null;

    const [prefix, ...valueParts] = tag.split(':');
    const value = valueParts.join(':'); // Handle cases with multiple colons

    // Format prefix: replace underscores with spaces and capitalize
    const formattedPrefix = prefix
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    // Format value: capitalize first letter
    const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);

    return { prefix: formattedPrefix, value: formattedValue };
  };

  if (tags.length === 0 && !editable) {
    return <div className={cn('text-muted-foreground text-sm', className)}>{t('tags.noTags')}</div>;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {visibleTags.map((tag, index) => {
        const formatted = formatTag(tag);
        return (
          <Badge
            key={index}
            variant="outline"
            className="group relative flex items-center gap-1 px-2 py-0.5 text-xs"
          >
            {formatted ? (
              <>
                <span className="font-medium">{formatted.prefix}:</span>
                <span>{formatted.value}</span>
              </>
            ) : (
              <span>{tag}</span>
            )}
            {editable && onTagRemove && (
              <button
                onClick={() => handleRemoveTag(tag)}
                className="ml-0.5 opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        );
      })}

      {hiddenCount > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-5 px-1.5 text-xs">
              +{hiddenCount} more
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">{t('tags.allTags')}</h4>
              <div className="flex max-h-60 flex-wrap gap-1.5 overflow-y-auto">
                {tags.map((tag, index) => {
                  const formatted = formatTag(tag);
                  return (
                    <Badge
                      key={index}
                      variant="outline"
                      className="group relative flex items-center gap-1 px-2 py-0.5 text-xs"
                    >
                      {formatted ? (
                        <>
                          <span className="font-medium">{formatted.prefix}:</span>
                          <span>{formatted.value}</span>
                        </>
                      ) : (
                        <span>{tag}</span>
                      )}
                      {editable && onTagRemove && (
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-0.5 opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {editable && onTagAdd && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-5 px-1.5">
              <Plus className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <div className="space-y-1">
                <h4 className="text-sm font-medium">{t('tags.addTag')}</h4>
                <p className="text-muted-foreground text-xs">{t('tags.tagFormat')}</p>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder={t('tags.tagPlaceholder')}
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddTag();
                    }
                  }}
                  disabled={isAdding}
                />
                <Button onClick={handleAddTag} disabled={!newTag.trim() || isAdding} size="sm">
                  {t('tags.addTag')}
                </Button>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">{t('tags.commonPrefixes')}</p>
                <div className="flex flex-wrap gap-1">
                  {[
                    { key: 'utm_source', label: 'UTM Source' },
                    { key: 'utm_campaign', label: 'UTM Campaign' },
                    { key: 'segment', label: 'Segment' },
                    { key: 'source', label: 'Source' },
                    { key: 'referral', label: 'Referral' },
                    { key: 'campaign', label: 'Campaign' },
                  ].map(({ key, label }) => (
                    <Badge
                      key={key}
                      variant="secondary"
                      className="cursor-pointer text-xs"
                      onClick={() => setNewTag(`${key}:`)}
                    >
                      {label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
